import { applySuggestedDependencies } from '../extensions/codemirror/claimDependencySuggestions.js';
import { claimsSettings } from '../stores/settingsStore.js';
import { get } from 'svelte/store';

/**
 * Service to handle automatic claim dependency updates
 * Monitors for new dependency suggestions and applies them if auto-update is enabled
 */
export class ClaimDependencyAutoUpdateService {
    constructor() {
        this.pendingUpdates = new Map();
        this.isProcessing = false;
    }
    
    /**
     * Check if auto-update is enabled and process dependencies
     * @param {Object} dependencies - Claim dependencies from backend
     * @param {EditorView} editorView - CodeMirror editor view
     * @returns {Object} - Summary of updates applied
     */
    async processAutoUpdates(dependencies, editorView) {
        // Check if auto-update is enabled
        const settings = get(claimsSettings);
        if (!settings.autoUpdateDependencies) {
            console.log('[ClaimDependencyAutoUpdate] Auto-update disabled, skipping');
            return { applied: false, count: 0 };
        }
        
        if (this.isProcessing) {
            console.log('[ClaimDependencyAutoUpdate] Already processing, skipping');
            return { applied: false, count: 0 };
        }
        
        this.isProcessing = true;
        const updates = [];
        
        try {
            // Get current document text to check existing dependencies
            const doc = editorView.state.doc;
            const text = doc.toString();
            
            // Parse existing dependencies
            const existingDeps = this.parseExistingDependencies(text);
            
            // Find claims that need updates
            Object.entries(dependencies).forEach(([claimNum, suggestedDeps]) => {
                const claimNumber = parseInt(claimNum);
                const existing = existingDeps.get(claimNumber);
                
                // Check if update is needed
                if (this.needsUpdate(existing, suggestedDeps)) {
                    updates.push({
                        claimNum: claimNumber,
                        current: existing || [],
                        suggested: suggestedDeps
                    });
                }
            });
            
            if (updates.length === 0) {
                console.log('[ClaimDependencyAutoUpdate] No updates needed');
                return { applied: true, count: 0 };
            }
            
            console.log(`[ClaimDependencyAutoUpdate] Applying ${updates.length} dependency updates`);
            
            // Apply updates in reverse order to maintain positions
            updates.sort((a, b) => b.claimNum - a.claimNum);
            
            let successCount = 0;
            for (const update of updates) {
                const success = applySuggestedDependencies(
                    editorView, 
                    update.claimNum, 
                    update.suggested
                );
                
                if (success) {
                    successCount++;
                    console.log(`[ClaimDependencyAutoUpdate] Updated claim ${update.claimNum}: [${update.current.join(',')}] → [${update.suggested.join(',')}]`);
                }
                
                // Small delay between updates to allow UI to update
                await new Promise(resolve => setTimeout(resolve, 50));
            }
            
            console.log(`[ClaimDependencyAutoUpdate] Successfully applied ${successCount}/${updates.length} updates`);
            
            return { 
                applied: true, 
                count: successCount,
                total: updates.length,
                updates: updates.map(u => ({
                    claim: u.claimNum,
                    from: u.current,
                    to: u.suggested
                }))
            };
            
        } catch (error) {
            console.error('[ClaimDependencyAutoUpdate] Error applying updates:', error);
            return { applied: false, count: 0, error: error.message };
        } finally {
            this.isProcessing = false;
        }
    }
    
    /**
     * Parse existing claim dependencies from document text
     * @param {string} text - Document text
     * @returns {Map} - Map of claim number to dependency array
     */
    parseExistingDependencies(text) {
        const dependencies = new Map();
        
        // Patterns to match existing dependencies
        const patterns = [
            // "The method of claim X"
            /(\d+)\.\s+The\s+(?:method|system|device|apparatus|process|composition|article)\s+of\s+claim\s+(\d+)/gi,
            // "The method of any of claims X-Y"
            /(\d+)\.\s+The\s+(?:method|system|device|apparatus|process|composition|article)\s+of\s+any\s+of\s+claims\s+(\d+)-(\d+)/gi,
            // "The method of claims X and Y"
            /(\d+)\.\s+The\s+(?:method|system|device|apparatus|process|composition|article)\s+of\s+claims\s+(\d+)\s+and\s+(\d+)/gi,
            // "The method according to claim X"
            /(\d+)\.\s+The\s+(?:method|system|device|apparatus|process|composition|article)\s+according\s+to\s+claim\s+(\d+)/gi,
            // "The method of any preceding claim"
            /(\d+)\.\s+The\s+(?:method|system|device|apparatus|process|composition|article)\s+of\s+any\s+preceding\s+claim/gi
        ];
        
        patterns.forEach((pattern, index) => {
            let match;
            pattern.lastIndex = 0; // Reset regex
            
            while ((match = pattern.exec(text)) !== null) {
                const claimNum = parseInt(match[1]);
                let deps = [];
                
                if (index === 1) {
                    // Range format
                    const start = parseInt(match[2]);
                    const end = parseInt(match[3]);
                    for (let i = start; i <= end; i++) {
                        if (i < claimNum) deps.push(i);
                    }
                } else if (index === 2) {
                    // "claims X and Y" format
                    deps = [parseInt(match[2]), parseInt(match[3])].filter(d => d < claimNum);
                } else if (index === 4) {
                    // "any preceding claim"
                    for (let i = 1; i < claimNum; i++) {
                        deps.push(i);
                    }
                } else if (index === 0 || index === 3) {
                    // Single claim reference
                    const dep = parseInt(match[2]);
                    if (dep < claimNum) deps.push(dep);
                }
                
                if (deps.length > 0) {
                    dependencies.set(claimNum, deps);
                }
            }
        });
        
        return dependencies;
    }
    
    /**
     * Check if a claim's dependencies need updating
     * @param {Array} existing - Current dependencies
     * @param {Array} suggested - Suggested dependencies
     * @returns {boolean} - True if update is needed
     */
    needsUpdate(existing, suggested) {
        if (!existing && suggested.length > 0) return true;
        if (!existing || !suggested) return false;
        
        // Sort both arrays for comparison
        const existingSorted = [...existing].sort((a, b) => a - b);
        const suggestedSorted = [...suggested].sort((a, b) => a - b);
        
        // Compare arrays
        if (existingSorted.length !== suggestedSorted.length) return true;
        
        for (let i = 0; i < existingSorted.length; i++) {
            if (existingSorted[i] !== suggestedSorted[i]) return true;
        }
        
        return false;
    }
}

// Export singleton instance
export const claimDependencyAutoUpdateService = new ClaimDependencyAutoUpdateService();